/**
 * Пользователи и роли `/users` (§9.4).
 *
 * Две вкладки одной страницы: список людей и матрица прав ролей.
 * Главное решение экрана — одноразовый пароль: сервер отдаёт его ровно один раз
 * (при создании и при сбросе), поэтому окно с паролем закрывается только явной
 * кнопкой и честно говорит, что повторно пароль не покажут.
 * Две защиты от выстрела в ногу держит сервер — себя нельзя отключить и нельзя
 * снять с себя права администратора; интерфейс не прячет эти действия, а
 * объясняет причину (§11.4) и показывает ответ сервера, если тот отказал.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import {
  AVATAR_COLORS, Avatar, Badge, Button, Checkbox, EmptyState, IconCheck, IconPlus, Input, Modal,
  SegmentedControl, Select, Skeleton, Tooltip, FieldRow, ConsequenceLine,
  type MenuItem, type SelectOption,
} from '../../components/index.js'
import { api } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { texts } from '../../texts.js'
import { AdminError, AdminPage, RowActions } from '../admin/AdminPage.js'
import { adminTexts } from '../admin/texts.admin.js'
import {
  errorText, formatDateTime, loadRoles, loadUsers,
  type AdminUserView, type PermissionView, type RolesView,
} from '../admin/admin-api.js'
import './UsersScreen.css'

const t = adminTexts.users

type Tab = 'users' | 'roles'

/** Одноразовый пароль: показываем один раз и только в этом окне. */
interface PasswordShow {
  lead: string
  password: string
}

interface CreateForm {
  fullName: string
  shortName: string
  email: string
  jobTitle: string
  roleCode: string | null
}

const EMPTY_FORM: CreateForm = {
  fullName: '', shortName: '', email: '', jobTitle: '', roleCode: null,
}

/** Права в матрице идут группами сервера: Данные → История → … (§9.4). */
function groupPermissions(permissions: PermissionView[]): { group: string; items: PermissionView[] }[] {
  const out: { group: string; items: PermissionView[] }[] = []
  for (const permission of permissions) {
    const last = out[out.length - 1]
    if (last && last.group === permission.group) last.items.push(permission)
    else out.push({ group: permission.group, items: [permission] })
  }
  return out
}

export function UsersScreen() {
  const toast = useApp((s) => s.toast)
  const me = useApp((s) => s.bootstrap?.user)

  const [tab, setTab] = useState<Tab>('users')
  const [users, setUsers] = useState<AdminUserView[] | null>(null)
  const [roles, setRoles] = useState<RolesView | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [roleOpenFor, setRoleOpenFor] = useState<number | null>(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM)
  const [createError, setCreateError] = useState<string | null>(null)
  const [roleFieldOpen, setRoleFieldOpen] = useState(false)

  const [password, setPassword] = useState<PasswordShow | null>(null)
  const [resetTarget, setResetTarget] = useState<AdminUserView | null>(null)
  /** Какие коды сейчас открыты на экране: по умолчанию ни одного. */
  const [shownCodes, setShownCodes] = useState<ReadonlySet<number>>(new Set())
  /** Коды и роли — отдельное полномочие: это доступ, а не карточка человека. */
  const mayAccess = Boolean(me?.permissions.includes('users.access'))
  /* Учётки для проверок стоят в списке рядом с людьми и сбивают с толку.
     Удалить их нельзя — на них ссылается история, — поэтому прячем. */
  const [withService, setWithService] = useState(false)

  /** Возвращает свежий список: после выдачи кода он нужен сразу, из состояния
      его ещё не прочитать. */
  const load = useCallback(async (): Promise<AdminUserView[] | null> => {
    setLoadError(null)
    try {
      const [list, rolesView] = await Promise.all([loadUsers(), loadRoles()])
      setUsers(list)
      setRoles(rolesView)
      return list
    } catch (err) {
      setLoadError(errorText(err))
      return null
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const roleOptions = useMemo<SelectOption[]>(
    () => (roles?.roles ?? []).map((role) => ({ value: role.code, label: role.name })),
    [roles],
  )

  /* Служебные учётки не показываем, пока их не попросят: они нужны
     автоматическим проверкам, а в списке людей читаются как лишние строки. */
  const shownUsers = (users ?? []).filter((u) => withService || !u.isService)
  const serviceCount = (users ?? []).filter((u) => u.isService).length
  const activeCount = shownUsers.filter((u) => u.isActive).length

  async function createUser() {
    const fullName = form.fullName.trim()
    const shortName = form.shortName.trim()
    const email = form.email.trim()
    const roleCode = form.roleCode
    if (!fullName || !shortName || !email || !roleCode) {
      setCreateError(adminTexts.common.required)
      return
    }
    setBusy(true)
    try {
      const res = await api.createUser({
        fullName,
        shortName,
        email,
        roleCode,
        ...(form.jobTitle.trim() ? { jobTitle: form.jobTitle.trim() } : {}),
      })
      setCreateOpen(false)
      setForm(EMPTY_FORM)
      setCreateError(null)
      toast('success', t.toastCreated(fullName))
      setPassword({ lead: t.passwordLead(fullName), password: res.password })
      await load()
    } catch (err) {
      setCreateError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function changeRole(user: AdminUserView, roleCode: string | null) {
    setRoleOpenFor(null)
    if (!roleCode || roleCode === user.roleCode) return
    try {
      await api.updateUser(user.id, { roleCode })
      const name = roleOptions.find((o) => o.value === roleCode)?.label ?? roleCode
      toast('success', t.toastRole(user.shortName, name))
      await load()
    } catch (err) {
      toast('error', errorText(err))
    }
  }

  /* Цвет кружка: по нему человека узнают в таблице, не читая имени.
     Комментарии владельца должны быть заметны издалека. */
  async function setColor(user: AdminUserView, color: string | null) {
    try {
      await api.updateUser(user.id, { color })
      await load()
    } catch (err) {
      toast('error', errorText(err))
    }
  }

  async function setActive(user: AdminUserView, isActive: boolean) {
    try {
      await api.updateUser(user.id, { isActive })
      toast('info', t.toastActive(user.shortName, isActive))
      await load()
    } catch (err) {
      toast('error', errorText(err))
    }
  }

  async function resetPassword(user: AdminUserView) {
    setBusy(true)
    try {
      const res = await api.resetUserPassword(user.id)
      setResetTarget(null)
      setPassword({ lead: t.passwordLeadReset(user.fullName), password: res.password })
      await load()
    } catch (err) {
      toast('error', errorText(err))
    } finally {
      setBusy(false)
    }
  }

  /** Выдать или сменить код: сразу кладём в буфер готовую ссылку-приглашение. */
  async function giveCode(user: AdminUserView) {
    setBusy(true)
    try {
      await api.setLoginCode(user.id)
      const fresh = await load()
      const updated = fresh?.find((item) => item.id === user.id) ?? null
      if (updated) await copyInvite(updated)
    } catch (err) {
      toast('error', errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function revokeCode(user: AdminUserView) {
    setBusy(true)
    try {
      await api.revokeLoginCode(user.id)
      await load()
      toast('success', t.codeRevoked(user.shortName))
    } catch (err) {
      toast('error', errorText(err))
    } finally {
      setBusy(false)
    }
  }

  /** Ссылка с кодом: человеку остаётся только нажать на неё. */
  async function copyInvite(user: AdminUserView) {
    if (!user.loginCode) return
    const link = `${window.location.origin}/login?code=${user.loginCode}`
    try {
      await navigator.clipboard.writeText(`${link}`)
      toast('success', t.codeCopied(user.shortName))
    } catch {
      toast('error', t.passwordCopyFailed)
    }
  }

  /**
   * Все ссылки разом: доступы раздают пачкой — семь человек, семь личек.
   * По одной кнопке на строку это семь заходов в меню и семь переключений
   * между вкладками.
   */
  async function copyAllInvites() {
    const shown = (users ?? []).filter((user) => user.isActive && !user.isService)
    const withCode = shown.filter((user) => user.loginCode)
    if (withCode.length === 0) { toast('error', t.copyAllNone); return }

    const lines = withCode.map(
      (user) => `${user.fullName} — ${window.location.origin}/login?code=${user.loginCode}`,
    )
    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      toast('success', t.copyAllDone(withCode.length))
      const missing = shown.filter((user) => !user.loginCode)
      if (missing.length > 0) {
        toast('info', t.copyAllMissing(missing.map((user) => user.shortName).join(', ')))
      }
    } catch {
      toast('error', t.passwordCopyFailed)
    }
  }

  async function copyPassword(value: string) {
    try {
      await navigator.clipboard.writeText(value)
      toast('success', t.passwordCopied)
    } catch {
      toast('error', t.passwordCopyFailed)
    }
  }

  function rowMenu(user: AdminUserView): MenuItem[] {
    const isSelf = user.id === me?.id
    return [
      ...(mayAccess ? [{ id: 'code', label: user.loginCode ? t.menuNewCode : t.menuGiveCode }] : []),
      ...(mayAccess && user.loginCode ? [{ id: 'revoke-code', label: t.menuRevokeCode, danger: true }] : []),
      { id: 'reset', label: t.menuReset, separatorBefore: mayAccess },
      {
        id: 'active',
        label: user.isActive ? t.menuDeactivate : t.menuActivate,
        danger: user.isActive,
        separatorBefore: true,
        disabled: isSelf && user.isActive,
        reason: isSelf && user.isActive ? t.selfDeactivateReason : undefined,
      },
    ]
  }

  const groups = useMemo(() => groupPermissions(roles?.permissions ?? []), [roles])

  return (
    <AdminPage
      wide
      title={texts.screen.users.title}
      meta={users ? t.count(shownUsers.length, activeCount) : undefined}
      subtitle={adminTexts.common.immediate}
      action={
        tab === 'users' ? (
          <>
          {mayAccess ? (
            <Button variant="secondary" onClick={() => void copyAllInvites()}>
              {t.copyAll}
            </Button>
          ) : null}
          <Button
            variant="primary"
            iconBefore={<IconPlus size={16} />}
            onClick={() => {
              setForm(EMPTY_FORM)
              setCreateError(null)
              setCreateOpen(true)
            }}
          >
            {t.add}
          </Button>
          </>
        ) : undefined
      }
    >
      <SegmentedControl<Tab>
        label={t.tabs.label}
        value={tab}
        onChange={setTab}
        options={[
          { value: 'users', label: t.tabs.users },
          { value: 'roles', label: t.tabs.roles },
        ]}
      />

      <div className="users__body">
        {loadError ? <AdminError message={loadError} onRetry={() => { void load() }} /> : null}

        {!loadError && users === null ? (
          <div className="adm-table-wrap">
            <Skeleton rows={7} columns={[200, 140, 200, 160, 160]} label={adminTexts.common.loading} />
          </div>
        ) : null}

        {!loadError && tab === 'users' && users !== null && users.length === 0 ? (
          <EmptyState title={t.emptyTitle} text={t.emptyText} />
        ) : null}

        {!loadError && tab === 'users' && users !== null && users.length > 0 ? (
          <div className="adm-table-wrap">
            <table className="adm-table users-table">
              <colgroup>
                <col className="users-col--name" />
                <col className="users-col--short" />
                <col className="users-col--code" />
                <col className="users-col--role" />
                <col className="users-col--job" />
                <col className="users-col--area" />
                <col className="users-col--active" />
                <col className="users-col--login" />
                <col className="users-col--actions" />
                <col className="users-col--menu" />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="t-col">{t.colName}</th>
                  <th scope="col" className="t-col">{t.colShortName}</th>
                  <th scope="col" className="t-col">{t.colColor}</th>
                  {/* Колонка «E-mail» убрана: на вход он не влияет (входят по
                      коду), а ширину забирал у роли и ФИО. */}
                  <th scope="col" className="t-col">{t.colLoginCode}</th>
                  <th scope="col" className="t-col">{t.colRole}</th>
                  <th scope="col" className="t-col">{t.colJob}</th>
                  <th scope="col" className="t-col">{t.colArea}</th>
                  <th scope="col" className="t-col">{t.colActive}</th>
                  <th scope="col" className="t-col">{t.colLastLogin}</th>
                  <th scope="col" className="t-col">
                    <span className="visually-hidden">{adminTexts.common.rowMenu}</span>
                  </th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {shownUsers.map((user) => {
                  const isSelf = user.id === me?.id
                  const lastLogin = formatDateTime(user.lastLoginAt)
                  return (
                    <tr key={user.id} className={user.isActive ? undefined : 'adm-row--archived'}>
                      <td>
                        <div className="adm-name">
                          <Avatar name={user.fullName} size={24} color={user.color ?? undefined} />
                          <span className="adm-name__text">{user.fullName}</span>
                          {/* Бейдж «пароль не сменён» убран: входят по коду, и
                              разговор о паролях сбивает с толку. Из-за него же
                              фамилии резались — он забирал половину колонки. */}
                        </div>
                      </td>
                      <td className="adm-cell--muted">{user.shortName}</td>
                      {/* Цвет кружка: восемь названных цветов, девятый —
                          «по имени», когда выделять человека не нужно. */}
                      <td>
                        <div className="users-colors">
                          <button
                            type="button"
                            className={user.color ? 'users-color users-color--auto' : 'users-color users-color--auto users-color--on'}
                            title={t.colorAuto}
                            aria-label={t.colorAuto}
                            onClick={() => void setColor(user, null)}
                          >
                            —
                          </button>
                          {AVATAR_COLORS.map((color) => (
                            <button
                              key={color}
                              type="button"
                              className={user.color === color ? 'users-color users-color--on' : 'users-color'}
                              data-color={color}
                              title={t.colorPick}
                              aria-label={`${t.colorPick}: ${color}`}
                              onClick={() => void setColor(user, color)}
                            />
                          ))}
                        </div>
                      </td>
                      {/* Код виден администратору: он его и пересылает. Рядом —
                          копирование готовой ссылки, чтобы не собирать её руками. */}
                      <td className="users-code">
                        {user.loginCode ? (
                          <span className="users-code__box">
                            <button
                              type="button"
                              className="users-code__value"
                              title={shownCodes.has(user.id) ? t.codeCopyLink : t.codeShow}
                              onClick={() => {
                                /* Код — действующий доступ, на экране он открыт
                                   всем, кто стоит за спиной. Первое нажатие
                                   показывает, второе копирует ссылку. */
                                if (!shownCodes.has(user.id)) {
                                  setShownCodes((previous) => new Set(previous).add(user.id))
                                  return
                                }
                                void copyInvite(user)
                              }}
                            >
                              {shownCodes.has(user.id) ? user.loginCode : '••••••'}
                            </button>
                          </span>
                        ) : (
                          <span className="adm-cell--muted">—</span>
                        )}
                      </td>
                      <td>
                        {/* Смена роли — раздача прав: её делает только тот, кто
                            отвечает за доступы. Остальные видят роль текстом. */}
                        {mayAccess ? (
                          <Select
                            label={t.roleSelect(user.shortName)}
                            options={roleOptions}
                            value={user.roleCode}
                            onChange={(value) => { void changeRole(user, value) }}
                            open={roleOpenFor === user.id}
                            onOpenChange={(open) => setRoleOpenFor(open ? user.id : null)}
                            allowEmpty={false}
                            size="cell"
                          />
                        ) : (
                          <span className="adm-cell--muted">{user.roleName}</span>
                        )}
                      </td>
                      <td className="adm-cell--muted">{user.jobTitle ?? '—'}</td>
                      <td className="adm-cell--muted">
                        {t.area[user.area ?? 'none'] ?? user.area}
                      </td>
                      <td>
                        {isSelf ? (
                          <Tooltip content={t.selfDeactivateReason}>
                            <Checkbox
                              checked={user.isActive}
                              disabled
                              aria-label={t.activeToggle(user.shortName)}
                              onChange={() => undefined}
                            />
                          </Tooltip>
                        ) : (
                          <Checkbox
                            checked={user.isActive}
                            aria-label={t.activeToggle(user.shortName)}
                            onChange={(checked) => { void setActive(user, checked) }}
                          />
                        )}
                      </td>
                      <td className="adm-cell--muted">{lastLogin ?? t.neverLoggedIn}</td>
                      <td className="adm-cell--actions">
                        <RowActions
                          label={`${adminTexts.common.rowMenu}: ${user.fullName}`}
                          items={rowMenu(user)}
                          onSelect={(id) => {
                            if (id === 'code') void giveCode(user)
                            if (id === 'revoke-code') void revokeCode(user)
                            if (id === 'reset') setResetTarget(user)
                            if (id === 'active') void setActive(user, !user.isActive)
                          }}
                        />
                      </td>
                      <td />
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {!loadError && tab === 'users' && users !== null && users.length > 0 ? (
          <div className="users-foot">
            <p className="adm-reason t-meta">{t.areaFromRole}</p>
            {serviceCount > 0 ? (
              <button
                type="button"
                className="users-service-toggle t-meta"
                onClick={() => setWithService((on) => !on)}
              >
                {withService ? t.serviceHide : t.serviceShow(serviceCount)}
              </button>
            ) : null}
          </div>
        ) : null}

        {!loadError && tab === 'roles' && roles !== null ? (
          <>
            <p className="adm-note">{t.rolesSeparationOff}</p>
            <p className="adm-note adm-note--muted">{t.rolesReadOnly}</p>

            <div className="adm-table-wrap">
              <table className="adm-table roles-table" aria-label={t.rolesMatrixLabel}>
                <thead>
                  <tr>
                    <th scope="col" className="t-col roles-table__head-cell">{t.rolesColPermission}</th>
                    {roles.roles.map((role) => (
                      <th scope="col" key={role.id} className="t-col roles-table__role">
                        <span className="roles-table__role-name">{role.name}</span>
                        <span className="roles-table__role-meta t-meta">
                          {role.isSystem ? t.rolesSystem : t.rolesUsersCount(role.usersCount)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => (
                    <Fragment key={group.group}>
                      <tr className="roles-table__group">
                        <th scope="colgroup" colSpan={roles.roles.length + 1} className="t-group">
                          {group.group}
                        </th>
                      </tr>
                      {group.items.map((permission) => (
                        <tr key={permission.code}>
                          <th scope="row" className="roles-table__permission">{permission.name}</th>
                          {roles.roles.map((role) => {
                            const has = role.permissions.includes(permission.code)
                            return (
                              <td key={role.id} className="adm-cell--center">
                                <span className="visually-hidden">
                                  {has
                                    ? t.rolesHas(role.name, permission.name)
                                    : t.rolesHasNot(role.name, permission.name)}
                                </span>
                                {has ? (
                                  <IconCheck size={16} className="roles-table__yes" />
                                ) : (
                                  <span className="roles-table__no" aria-hidden="true">—</span>
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>

      {/* ───────────────────────── новый пользователь ───────────────────────── */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t.createTitle}
        size="m"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {adminTexts.common.cancel}
            </Button>
            <Button variant="primary" loading={busy} onClick={() => { void createUser() }}>
              {t.createSubmit}
            </Button>
          </>
        }
      >
        <div className="adm-modal-stack">
          <FieldRow label={t.fullName} htmlFor="user-full-name">
            <Input
              id="user-full-name"
              autoFocus
              value={form.fullName}
              placeholder={t.fullNamePlaceholder}
              onChange={(event) => setForm({ ...form, fullName: event.target.value })}
            />
          </FieldRow>
          <FieldRow label={t.shortName} htmlFor="user-short-name" consequence={<ConsequenceLine>{t.shortNameHint}</ConsequenceLine>}>
            <Input
              id="user-short-name"
              value={form.shortName}
              onChange={(event) => setForm({ ...form, shortName: event.target.value })}
            />
          </FieldRow>
          <FieldRow label={t.email} htmlFor="user-email" consequence={<ConsequenceLine>{t.emailHint}</ConsequenceLine>}>
            <Input
              id="user-email"
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </FieldRow>
          <FieldRow label={t.jobTitle} htmlFor="user-job">
            <Input
              id="user-job"
              value={form.jobTitle}
              onChange={(event) => setForm({ ...form, jobTitle: event.target.value })}
            />
          </FieldRow>
          <FieldRow label={t.role} consequence={<ConsequenceLine>{t.roleHint}</ConsequenceLine>} error={createError ?? undefined} group>
            <Select
              label={t.role}
              options={roleOptions}
              value={form.roleCode}
              onChange={(value) => setForm({ ...form, roleCode: value })}
              open={roleFieldOpen}
              onOpenChange={setRoleFieldOpen}
              allowEmpty={false}
            />
          </FieldRow>
          <ConsequenceLine>{t.passwordWarning}</ConsequenceLine>
        </div>
      </Modal>

      {/* ───────────────────────── одноразовый пароль ───────────────────────── */}
      <Modal
        open={password !== null}
        onClose={() => setPassword(null)}
        title={t.passwordTitle}
        size="s"
        dismissible={false}
        footerNote={t.passwordOnce}
        footer={
          <Button variant="primary" onClick={() => setPassword(null)}>
            {adminTexts.common.close}
          </Button>
        }
      >
        {password ? (
          <div className="adm-modal-stack">
            <p className="adm-modal-text">{password.lead}</p>
            <div className="users-password">
              <span className="visually-hidden">{t.passwordLabel}</span>
              <span className="users-password__value">{password.password}</span>
              <Button variant="secondary" onClick={() => { void copyPassword(password.password) }}>
                {adminTexts.common.copy}
              </Button>
            </div>
            <ConsequenceLine tone="danger">{t.passwordWarning}</ConsequenceLine>
          </div>
        ) : null}
      </Modal>

      {/* ───────────────────────── сброс пароля ───────────────────────── */}
      <Modal
        open={resetTarget !== null}
        onClose={() => setResetTarget(null)}
        title={t.resetTitle}
        size="s"
        dismissible={false}
        footer={
          <>
            <Button variant="secondary" onClick={() => setResetTarget(null)}>
              {adminTexts.common.cancel}
            </Button>
            <Button
              variant="danger-ghost"
              loading={busy}
              onClick={() => { if (resetTarget) void resetPassword(resetTarget) }}
            >
              {t.resetSubmit}
            </Button>
          </>
        }
      >
        {resetTarget ? (
          <>
            <p className="adm-modal-text">{t.resetQuestion(resetTarget.fullName)}</p>
            <p className="adm-modal-text">{t.resetExplain}</p>
          </>
        ) : null}
      </Modal>
    </AdminPage>
  )
}
